import React from 'react';

import styles from './Table.module.scss';

/**
 * テーブルの列定義。
 * @template T - 行データの型
 */
export type TableColumn<T> = {
  /** 列の識別子（key prop に使用） */
  key: string;
  /** ヘッダーセルの内容 */
  header: React.ReactNode;
  /** セルのレンダラー */
  render: (row: T) => React.ReactNode;
  /** 列幅（colgroup に適用。例: "40%", "120px"）。省略時はブラウザが自動配分。 */
  width?: string;
};

/**
 * プロパティ
 * @template T - 行データの型
 */
export type TableProps<T> = {
  /** 列定義の配列 */
  columns: TableColumn<T>[];
  /** 表示する行データの配列 */
  rows: T[];
  /** 行の一意キーを返す関数 */
  getRowKey: (row: T) => string;
};

/**
 * テーブルの実装本体。
 * 列定義（TableColumn）を props で受け取り、ヘッダーと行を描画する。
 * スタイルは Elements/Table のスコープで管理するため、各 feature が個別に定義する必要はない。
 * @template T - 行データの型
 * @internal エクスポートは {@link Table}（React.memo 適用済み）を使うこと。
 */
const TableInner = <T,>({
  columns,
  rows,
  getRowKey,
}: TableProps<T>): React.ReactElement => (
  <div className={styles['table-wrapper']}>
    <table className={styles.table}>
      {columns.some((col) => col.width) && (
        <colgroup>
          {columns.map((col) => (
            <col
              key={col.key}
              style={col.width ? { width: col.width } : undefined}
            />
          ))}
        </colgroup>
      )}
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={getRowKey(row)}>
            {columns.map((col) => (
              <td key={col.key}>{col.render(row)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * 汎用テーブルコンポーネント。
 * {@link TableInner} に React.memo を適用したエクスポート用コンポーネント。
 * `React.memo` はジェネリクスを消してしまうため、`as typeof TableInner` で元の型に戻している。
 */
export const Table = React.memo(TableInner) as typeof TableInner;
